import { Link } from 'react-router-dom'
import { Plane, Monitor, Smartphone, Tv, Grid3X3, LayoutDashboard, Map, Minimize2, Zap, Bell, Palette, Code, ChevronRight, Check } from 'lucide-react'

const MODE_SHOWCASE = [
  {
    key: 'dashboard',
    icon: <LayoutDashboard className="w-5 h-5" />,
    title: 'Dashboard',
    tagline: 'The full picture, all at once',
    color: 'cyan',
    image: '/screenshots/dashboard.png',
    features: [
      'Every aircraft in range, tap any one to inspect it',
      'Altitude, ground speed, heading and vertical rate',
      'Live METAR weather and position alongside the flight',
    ],
  },
  {
    key: 'matrix',
    icon: <Grid3X3 className="w-5 h-5" />,
    title: 'Matrix',
    tagline: 'Retro LED aesthetic, one flight at a time',
    color: 'matrix',
    image: '/screenshots/matrix.png',
    features: [
      'Auto-rotates through every aircraft in your sector',
      'Callsign, altitude, speed, track and vertical rate',
      'Built for physical LED matrix panels',
    ],
  },
  {
    key: 'fids',
    icon: <Map className="w-5 h-5" />,
    title: 'Airport FIDS',
    tagline: 'A real departure board for any airport',
    color: 'amber',
    image: '/screenshots/fids.png',
    features: [
      'Gate, terminal and status, refreshed every 60 seconds',
      'Airline logos and live delay or cancellation tracking',
      'Scrollable board, built for busy hubs, not just quiet ones',
    ],
  },
  {
    key: 'radar',
    icon: <Map className="w-5 h-5" />,
    title: 'Radar Map',
    tagline: 'A top-down sweep centered on you',
    color: 'emerald',
    image: '/screenshots/radar.png',
    features: [
      'True bearing and distance for every contact, not a guess',
      'Colour-coded by altitude band at a glance',
      'Configurable range from 5 to 100 nautical miles',
    ],
  },
  {
    key: 'minimal',
    icon: <Minimize2 className="w-5 h-5" />,
    title: 'Minimal',
    tagline: 'One flight, huge and legible',
    color: 'rose',
    image: '/screenshots/minimal.png',
    features: [
      'Airline branding, callsign, altitude, speed and distance',
      'Pure black background, built for OLED and ambient screens',
      'Cycles through nearby aircraft automatically',
    ],
  },
]

export default function Home() {
  return (
    <div className="min-h-screen bg-slate-950">
      <nav className="border-b border-slate-800/50 bg-slate-950/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-flight-cyan/20 flex items-center justify-center">
              <Plane className="w-5 h-5 text-flight-cyan" />
            </div>
            <span className="text-xl font-bold text-white tracking-tight">FlightWall</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="#features" className="text-slate-400 hover:text-white text-sm transition-colors">Features</a>
            <a href="#modes" className="text-slate-400 hover:text-white text-sm transition-colors">Display Modes</a>
            <Link to="/pricing" className="text-slate-400 hover:text-white text-sm transition-colors">Pricing</Link>
            <Link to="/dashboard" className="px-4 py-2 rounded-lg bg-flight-cyan/20 text-flight-cyan text-sm font-medium hover:bg-flight-cyan/30 transition-colors">Get Started</Link>
          </div>
        </div>
      </nav>
      
      <section className="relative pt-20 pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-flight-cyan/10 via-slate-950 to-slate-950" />
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-flight-cyan/10 border border-flight-cyan/20 text-flight-cyan text-xs font-medium mb-6">
              <Zap className="w-3 h-3" />Now in public beta, free tier available
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white tracking-tight leading-[1.1] mb-6">
              Turn any screen into a<br /><span className="text-flight-cyan">live flight tracker</span>
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed mb-8 max-w-2xl">
              No hardware required. FlightWall transforms your TV, monitor, tablet, or LED matrix into a real-time aircraft display. Matrix, dashboard, airport FIDS, radar map, and minimal modes, all from one account.
            </p>
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="px-8 py-4 rounded-xl bg-flight-cyan text-slate-950 font-bold hover:bg-flight-cyan/90 transition-colors flex items-center gap-2">
                Create Free Display <ChevronRight className="w-4 h-4" />
              </Link>
              <a href="#modes" className="px-8 py-4 rounded-xl border border-slate-700 text-white font-medium hover:bg-slate-800 transition-colors">See Demo</a>
            </div>
            <div className="flex items-center gap-6 mt-12 text-slate-500 text-sm">
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" />No subscription required for basic use</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" />Works on any device with a browser</div>
            </div>
          </div>
        </div>
      </section>
      
      <section id="modes" className="py-24 border-t border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Five Ways to Watch the Sky</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">Every mode below is a live screenshot of the actual product, not a mockup. Pick one per display, switch anytime.</p>
          </div>
          <div className="space-y-24">
            {MODE_SHOWCASE.map((mode, i) => <ModeShowcaseRow key={mode.key} mode={mode} reversed={i % 2 === 1} />)}
          </div>
        </div>
      </section>

      <section id="features" className="py-24 border-t border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Built for Aviation Enthusiasts & Businesses</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard icon={<Monitor className="w-6 h-6" />} title="Multi-Screen Output" desc="One account, unlimited displays. Each gets a unique URL." />
            <FeatureCard icon={<Smartphone className="w-6 h-6" />} title="PWA & Mobile" desc="Install as native app. Chromecast and AirPlay support." />
            <FeatureCard icon={<Bell className="w-6 h-6" />} title="Smart Alerts" desc="Get notified when specific aircraft pass overhead." />
            <FeatureCard icon={<Palette className="w-6 h-6" />} title="Custom Branding" desc="Upload logo, choose colors, remove FlightWall branding." />
            <FeatureCard icon={<Code className="w-6 h-6" />} title="Developer API" desc="REST API and WebSocket streaming for custom integrations." />
            <FeatureCard icon={<Tv className="w-6 h-6" />} title="Weather Overlay" desc="Live METAR/TAF data alongside flight information." />
          </div>
        </div>
      </section>
      
      <section className="py-24 border-t border-slate-800/50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to cast the sky?</h2>
          <p className="text-slate-400 mb-8">Start with a free display. Upgrade when you need more screens.</p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/dashboard" className="px-8 py-4 rounded-xl bg-flight-cyan text-slate-950 font-bold hover:bg-flight-cyan/90 transition-colors">Create Your First Display</Link>
            <Link to="/pricing" className="px-8 py-4 rounded-xl border border-slate-700 text-white font-medium hover:bg-slate-800 transition-colors">View Pricing</Link>
          </div>
        </div>
      </section>
      
      <footer className="border-t border-slate-800/50 py-12">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-2"><Plane className="w-5 h-5 text-slate-600" /><span className="text-slate-500 text-sm">FlightWall © 2026</span></div>
          <div className="flex items-center gap-6 text-slate-500 text-sm">
            <a href="#" className="hover:text-white transition-colors">Privacy</a>
            <a href="#" className="hover:text-white transition-colors">Terms</a>
            <a href="#" className="hover:text-white transition-colors">API Docs</a>
          </div>
        </div>
      </footer>
    </div>
  )
}

const MODE_COLORS = {
  cyan: { badge: 'text-flight-cyan border-flight-cyan/30 bg-flight-cyan/10', frame: 'hover:border-flight-cyan/40' },
  matrix: { badge: 'text-matrix-green border-matrix-green/30 bg-matrix-green/10', frame: 'hover:border-matrix-green/40' },
  amber: { badge: 'text-flight-amber border-flight-amber/30 bg-flight-amber/10', frame: 'hover:border-flight-amber/40' },
  emerald: { badge: 'text-flight-emerald border-flight-emerald/30 bg-flight-emerald/10', frame: 'hover:border-flight-emerald/40' },
  rose: { badge: 'text-flight-rose border-flight-rose/30 bg-flight-rose/10', frame: 'hover:border-flight-rose/40' },
}

function ModeShowcaseRow({ mode, reversed }) {
  const colors = MODE_COLORS[mode.color]
  return (
    <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
      <div className={`${reversed ? 'md:order-2' : ''} rounded-2xl overflow-hidden border border-slate-800 bg-slate-900 transition-colors duration-300 ${colors.frame}`}>
        <img src={mode.image} alt={`FlightWall ${mode.title} display mode showing live flight data`} className="w-full h-auto block" loading="lazy" />
      </div>
      <div className={reversed ? 'md:order-1' : ''}>
        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-medium mb-4 ${colors.badge}`}>
          {mode.icon}{mode.title}
        </div>
        <h3 className="text-2xl md:text-3xl font-bold text-white mb-6 tracking-tight">{mode.tagline}</h3>
        <ul className="space-y-3">
          {mode.features.map((f, i) => (
            <li key={i} className="flex items-start gap-3 text-slate-400">
              <Check className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

function FeatureCard({ icon, title, desc }) {
  return (
    <div className="p-6 rounded-2xl border border-slate-800 bg-slate-900/50 hover:border-slate-700 transition-all">
      <div className="text-flight-cyan mb-4">{icon}</div>
      <h3 className="text-white font-bold mb-2">{title}</h3>
      <p className="text-slate-400 text-sm leading-relaxed">{desc}</p>
    </div>
  )
}
